import type { CreativeFamily } from './creative-api';

/** A formats job's completeness target: the version it fills (`parent_id`) and the total distinct slot
 *  count it should reach (the job's `total`, which counts the main plus every drawn format). */
export interface FormatTarget {
  versionId: string;
  total: number;
}

/**
 * Of the given targets, the ones the family does NOT yet satisfy: the version is absent, or holds fewer
 * than `total` slots.
 *
 * Drives the post-completion poll. The format images never travel over the websocket — the FE rebuilds a
 * version's `formats` map purely from the family read, and the ws frames only say WHEN to re-read. So a
 * version can be stranded one slot short two ways: the family read lands a beat before the last drawn slot
 * is visible (read-after-write lag), or a dropped frame means the re-read is never triggered. Either way
 * the family is re-read until this returns empty (every target version holds all its slots) or the poll
 * gives up.
 */
export const formatTargetsStillIncomplete = (
  family: CreativeFamily | undefined,
  targets: FormatTarget[],
): FormatTarget[] => {
  const versions = family?.placements.flatMap((p) => p.items).flatMap((it) => it.versions ?? []) ?? [];
  return targets.filter((t) => {
    const version = versions.find((v) => v.id === t.versionId);
    return !version || Object.keys(version.formats ?? {}).length < t.total;
  });
};
